
import Vue from 'vue';
import { Runnit, RUNNITS_ACCESS_LEVEL, RUNNIT_TYPE } from '@run-diffusion/shared';
import { mapState, mapActions } from 'vuex';
import { db, FieldValue, Query } from '@/firebase';
import { buildFeaturedTemplatesQuery, FEATURED_TEMPLATES_GRID_LIMIT, nextFeaturedOrder } from '@/utils/featuredTemplates';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import { TeamUserMixin, RunnitsCRUDMixin } from '@/mixins';
import * as amplitude from '@amplitude/analytics-browser';

export default Vue.extend({
	name: 'ManageRunnitMenuBtn',
	mixins: [
		TeamUserMixin,
		RunnitsCRUDMixin,
	],
	props: {
		buttonProps: {
			type: Object,
			default: () => ({
				fab: true,
				icon: true,
				small: true,
			}),
		},
		runnit: {
			type: Object as () => Runnit,
			required: true,
		},
		buttonClass: {
			type: String,
			default: 'mr-3',
		},
		showAccessSettings: {
			type: Boolean,
			default: true,
		},
		icon: {
			type: String,
			default: 'mdi-cog-outline',
		},
		iconColor: {
			type: String,
			default: 'var(--grey-500)',
		},
		isOpen: { // Control externally
			type: Boolean,
			default: false,
		},
		isTemplate: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			MORE_ACTIONS_ITEM_VALUE: {
				EDIT: 'edit',
				MANAGE_ACCESS: 'manage-access',
				DELETE: 'delete',
				CREATE_TEMPLATE: 'create-template',
				CREATE_BOARD: 'create-board',
				FEATURE: 'feature',
				UNFEATURE: 'unfeature',
			},
		};
	},
	computed: {
		...mapState([
			'user',
			'runnitState',
		]),
		isRunnitOwner (): boolean {
			return !!(
				this.user &&
				this.runnit?.userId &&
				this.runnit.userId === this.user.id
			);
		},
		canEditOrDelete (): boolean {
			// For featured templates, only admins can edit/delete
			if (this.isTemplate && this.runnit?.isFeatured) {
				return !!this.user?.isAdmin;
			}
			return this.isRunnitOwner || this.hasManageAllGenerationsPermission;
		},
		canChangeOwner (): boolean {
			return this.canChangeRunnitOwner(this.runnit);
		},
		canManageAccess (): boolean {
			// For featured templates, only admins can manage access
			if (this.isTemplate && this.runnit?.isFeatured) {
				return !!this.user?.isAdmin;
			}
			return !!(
				(
					// Can switch it to PRIVATE without a permission if I'm the owner
					this.isRunnitOwner &&
					this.runnit?.accessLevel !== RUNNITS_ACCESS_LEVEL.PRIVATE
				) ||
				(
					(this.isRunnitOwner || this.hasManageAllGenerationsPermission) &&
					this.hasShareRunnitAssetsPermission
				)
			);
		},
		moreActionsMenuItems () {
			let idIncrementer: number = 0;
			const items: any[] = [];

			// Edit - requires canEditOrDelete
			if (this.canEditOrDelete) {
				items.push({
					id: idIncrementer++,
					icon: 'mdi-pencil-outline',
					label: 'Edit',
					description: this.isTemplate ? 'Configure board template settings' : 'Configure board settings',
					value: this.MORE_ACTIONS_ITEM_VALUE.EDIT,
				});
			}

			// Create Board - only for templates
			if (this.isTemplate) {
				items.push({
					id: idIncrementer++,
					icon: 'mdi-plus-circle-outline',
					label: 'New Board',
					description: 'Create a new board based on this template',
					value: this.MORE_ACTIONS_ITEM_VALUE.CREATE_BOARD,
				});
			}

			// Feature/Unfeature Template - admin-only, template-only
			if (this.isTemplate && this.user?.isAdmin) {
				if (this.runnit?.isFeatured) {
					items.push({
						id: idIncrementer++,
						icon: 'mdi-star-off',
						label: 'Unfeature Template',
						description: 'Remove this template from featured templates',
						value: this.MORE_ACTIONS_ITEM_VALUE.UNFEATURE,
						iconStyle: { color: 'var(--admin-color)' },
					});
				} else {
					items.push({
						id: idIncrementer++,
						icon: 'mdi-star',
						label: 'Feature Template',
						description: 'Make this template visible to all users',
						value: this.MORE_ACTIONS_ITEM_VALUE.FEATURE,
						iconStyle: { color: 'var(--admin-color)' },
					});
				}
			}

			// Access Settings - requires canManageAccess or canChangeOwner, AND team context
			const isTeamContext: boolean = !!(
				this.showAccessSettings &&
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
			);
			if (isTeamContext && (this.canManageAccess || this.canChangeOwner)) {
				items.push({
					id: idIncrementer++,
					icon: 'mdi-account-multiple-plus-outline',
					label: 'Access Settings',
					description: this.canChangeOwner ? 'Manage owner, access level & members' : 'Manage access level & members',
					value: this.MORE_ACTIONS_ITEM_VALUE.MANAGE_ACCESS,
				});
			}
			if (isTeamContext && this.canManageAccess) {
				items.push({
					id: idIncrementer++,
					icon: this.isTemplate ? 'mdi-content-duplicate' : 'mdi-file-document-plus-outline',
					label: this.isTemplate ? 'Duplicate Template' : 'Create Template',
					description: this.isTemplate ? 'Make a new template using this as a starting point' : 'Save as a template, for quick creation of new boards',
					value: this.MORE_ACTIONS_ITEM_VALUE.CREATE_TEMPLATE,
				});
			}

			// Delete - requires canEditOrDelete
			if (this.canEditOrDelete) {
				items.push({
					id: idIncrementer++,
					icon: 'mdi-delete-outline',
					iconStyle: { color: 'var(--red-500)' },
					descriptionStyle: { color: 'var(--grey-400)' },
					label: 'Delete',
					description: this.isTemplate ? 'Delete this board template' : 'Delete this board',
					value: this.MORE_ACTIONS_ITEM_VALUE.DELETE,
				});
			}

			return items;
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
		]),
		onSelectedMoreActions (moreActionsItemValue: string): void {
			switch (moreActionsItemValue) {
				case this.MORE_ACTIONS_ITEM_VALUE.EDIT:
					if (this.isTemplate) {
						// For templates, open the template dialog in edit mode
						this.updateRunnitState({
							createRunnitTemplateOpen: true,
							duplicateTemplate: false,
							runnit: this.runnit,
						});
					} else {
						// For regular runnits, open the configure dialog
						this.updateRunnitState({
							runnitDraft: null,
							runnitDraftNodeRef: null,
							runnit: this.runnit,
							configureRunnitOpen: true,
						});
					}
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.MANAGE_ACCESS:
					this.updateRunnitState({
						runnitManageAccessOpen: true,
						runnit: this.runnit,
					});
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.DELETE:
					this.updateRunnitState({
						runnitDeleteOpen: true,
						runnit: this.runnit,
					});
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.CREATE_TEMPLATE:
					amplitude.track(`Open ${this.isTemplate ? 'Duplicate' : 'Create'} Template Dialog`, { location: 'ManageRunnitMenuBtn' });
					this.updateRunnitState({
						createRunnitTemplateOpen: true,
						// If starting from a template, this should create a new template (not edit the existing one)
						duplicateTemplate: !!this.isTemplate,
						runnit: this.runnit,
					});
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.CREATE_BOARD:
					this.createBoardFromTemplate();
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.FEATURE:
					this.onFeatureTemplate(true);
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.UNFEATURE:
					this.onFeatureTemplate(false);
					break;
				default:
					break;
			}
		},
		async onFeatureTemplate (isFeatured: boolean): Promise<void> {
			if (!this.runnit || !this.user?.isAdmin) return;

			try {
				if (isFeatured) {
					// A featured template with no featuredOrder is dropped by the
					// carousel's sparse index, so the order is assigned in the same
					// write that features it. New templates go to the end; admins
					// reorder from Admin > Featured Templates.
					//
					// The whole featured set is read to find the highest order. A
					// descending limit(1) would be cheaper but the composite index is
					// ascending only, so that shape fails with FAILED_PRECONDITION.
					// The set is small (tens of documents against this cap).
					//
					// Read-then-write: two admins featuring at the same moment both
					// compute the same order and one duplicate results. Accepted
					// rather than wrapped in a transaction, because the duplicate is
					// cosmetic (an arbitrary tie-break between two adjacent cards)
					// and the next drag-reorder renumbers it away.
					const featuredRef: Query = buildFeaturedTemplatesQuery(
						db.collection('runnits'),
						FEATURED_TEMPLATES_GRID_LIMIT,
					);
					const featuredSnap = await featuredRef.get();
					const featured: Runnit[] = featuredSnap.docs.map((doc: any) => ({ ...doc.data(), id: doc.id }));

					await db.collection('runnits').doc(this.runnit.id).update({
						isFeatured: true,
						featuredOrder: nextFeaturedOrder(featured),
					});
				} else {
					await db.collection('runnits').doc(this.runnit.id).update({
						isFeatured: false,
						featuredOrder: FieldValue.delete(),
					});
				}
				this.updateSnackbar({
					message: isFeatured ? 'Template featured successfully' : 'Template unfeatured successfully',
					status: SNACKBAR_STATUS.SUCCESS,
				});
			} catch (error) {
				console.error('Error updating featured status:', error);
				this.updateSnackbar({
					message: 'Failed to update featured status',
					status: SNACKBAR_STATUS.ERROR,
				});
			}
		},
		async createBoardFromTemplate (): Promise<void> {
			try {
				// Check if user can create a new runnit (this will show upsell dialog if limit reached)
				if (!this.canCreateNewRunnit(false)) {
					return;
				}

				amplitude.track(`Open Create Board from Template`, { location: 'ManageRunnitMenuBtn' });
				// Create a draft runnit based on the template
				const clonedRunnit = {
					...this.runnit,
					userId: this.user.id, // Set the current user as owner
					title: `${this.runnit.title} (Copy)`, // Add (Copy) to distinguish from template
					createdAt: new Date(),
					deletedAt: null,
					// Reset template-specific fields
					type: RUNNIT_TYPE.WORKFLOW,
					accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE,
					teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? this.team?.id : null,
				};
				delete clonedRunnit.id;

				// Update the runnit state to open the edit dialog with the cloned runnit
				this.updateRunnitState({
					runnitDraft: clonedRunnit,
					runnitDraftNodeRef: null,
					configureRunnitOpen: true,
					// Set a flag to indicate this is a template clone
					cloneFromTemplate: true,
					templateRunnit: this.runnit,
				});
			} catch (error) {
				console.error('Error creating board from template:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error creating board from template, please try again',
					show: true,
				});
			}
		},
	},
	components: {
		BaseStyledMenu,
	},
});
